import React, { useState, useEffect, useContext } from 'react';
import { formatHoursToHoursMinutes, formatDecimalHours } from '../../utils/timeUtils';
import { Container, Row, Col, Form, Button, Card, Alert, InputGroup, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import reportService from '../../services/reportService';
import projectService from '../../services/projectService';

const EditReport = () => {
  const { id } = useParams();
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const navigate = useNavigate();
  
  // Function to normalize hours input - handles minutes over 59 (rolling over to next hour)
  const normalizeHoursInput = (value) => {
    if (!value || value === '') return '0.00';
    
    // Don't allow negative values
    if (parseFloat(value) < 0) return '0.00';
    
    // If it has a decimal part
    if (value.toString().includes('.')) {
      const parts = value.toString().split('.');
      let hours = parseInt(parts[0], 10) || 0;
      let minutes = parts[1];
      
      // Handle the minutes part
      if (minutes.length === 1) {
        // Treat single digit as tens of minutes (e.g., 0.4 = 0.40 = 40 minutes)
        minutes = parseInt(minutes, 10) * 10 || 0;
      } else {
        // Parse regular minutes
        minutes = parseInt(minutes, 10) || 0;
      }
      
      // Handle rollover if minutes >= 60
      if (minutes >= 60) {
        hours += Math.floor(minutes / 60);
        minutes = minutes % 60;
      }
      
      // Format to hours.minutes (with leading zeros for minutes if needed)
      return `${hours}.${minutes.toString().padStart(2, '0')}`;
    }
    
    // No decimal, just whole hours
    return `${parseInt(value, 10) || 0}.00`;
  };
  
  // Fetch the report to edit
  // useEffect(() => {
    // const fetchReport = async () => {
      // try {
  //     try {
  //       setLoadingReport(true);
  //       const data = await reportService.getReportById(id);
  //       setReport(data.report);
        
  //       // Check if the report was created today (only allow same-day edits)
  //       const reportDate = new Date(data.report.date).toDateString();
  //       const today = new Date().toDateString();
        
  //       if (reportDate !== today) {
  //         setSubmitError('Reports can only be edited on the same day they were created.');
  //         setTimeout(() => {
  //           navigate('/reports');
  //         }, 3000);
  //         return;
  //       }
  //     } catch (err) {
  //       console.error('Error fetching report:', err);
  //       setSubmitError('Failed to load report. Please try again.');
  //     } finally {
  //       setLoadingReport(false);
  //     }
  //   };
    
  //   fetchReport();
  // }, [id, navigate]);

useEffect(() => {
  const fetchReport = async () => {
    try {
      setLoadingReport(true);
      const data = await reportService.getReportById(id);
      setReport(data.report);

      const reportDateObj = new Date(data.report.date);
      const today = new Date();
      const yesterday = new Date();
      const dayBeforeYesterday = new Date();

      yesterday.setDate(today.getDate() - 1);
      dayBeforeYesterday.setDate(today.getDate() - 2);

      const formatDate = (d) => d.toISOString().split("T")[0];
      const reportDateStr = formatDate(reportDateObj);
      const todayStr = formatDate(today);
      const yesterdayStr = formatDate(yesterday);
      const dayBeforeYesterdayStr = formatDate(dayBeforeYesterday);

      const isEditable =
        reportDateStr === todayStr ||
        reportDateStr === yesterdayStr ||
        reportDateStr === dayBeforeYesterdayStr;

      if (!isEditable) {
        setSubmitError("Reports can only be edited within 3 days of creation.");
        setTimeout(() => {
          navigate("/reports");
        }, 3000);
        return;
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setSubmitError("Failed to load report. Please try again.");
    } finally {
      setLoadingReport(false);
    }
  };

  fetchReport();
}, [id, navigate]);


  
  // Fetch projects when component mounts
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Get projects assigned to the current developer
        const assignedProjects = await projectService.getMyProjects();
        
        // If developer has no assigned projects, get all projects as fallback
        if (assignedProjects.length === 0) {
          const allProjects = await projectService.getAllProjects();
          setProjects(allProjects);
        } else {
          setProjects(assignedProjects);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProjects();
  }, []);
  
  // Validation schema
  const validationSchema = Yup.object({
    date: Yup.date().required('Date is required'),
    project_id: Yup.number().required('Project selection is required'),
    tasks: Yup.string().required('Summary of tasks is required'),
    task_details: Yup.array().of(
      Yup.object().shape({
        description: Yup.string().required('Task description is required'),
        hours: Yup.number()
          .required('Hours are required')
          .positive('Hours must be positive')
          .max(24, 'Hours cannot exceed 24'),
        project_id: Yup.number().required('Project selection is required')
      })
    ).min(1, 'At least one task is required')
  });
  
  // Calculate total hours from task details
  const calculateTotalHours = (taskDetails) => {
    let totalHours = 0;
    let totalMinutes = 0;
    
    taskDetails.forEach(task => {
      if (!task.hours) return;
      
      const hoursStr = task.hours.toString();
      
      if (hoursStr.includes('.')) {
        const parts = hoursStr.split('.');
        const wholeHours = parseInt(parts[0], 10) || 0;
        const decimalPart = parts[1];
        
        // Add the whole hours to total
        totalHours += wholeHours;
        
        // Always treat decimal part as direct minutes (e.g., 1.40 means 1 hour 40 minutes)
        // If it's a single digit, treat as tens of minutes (e.g., 1.4 means 1 hour 40 minutes)
        if (decimalPart.length === 1) {
          totalMinutes += parseInt(decimalPart, 10) * 10 || 0;
        } else {
          totalMinutes += parseInt(decimalPart, 10) || 0;
        }
      } else {
        // No decimal, just whole hours
        totalHours += parseInt(hoursStr, 10) || 0;
      }
    });
    
    // Handle minute rollover to hours (60 minutes = 1 hour)
    if (totalMinutes >= 60) {
      totalHours += Math.floor(totalMinutes / 60);
      totalMinutes = totalMinutes % 60;
    }
    
    // Format as decimal hours with minutes as the decimal part (e.g., 3.05 for 3 hours 5 minutes)
    if (totalMinutes === 0) {
      return totalHours.toFixed(2);
    } else {
      const formattedMinutes = totalMinutes.toString().padStart(2, '0');
      return `${totalHours}.${formattedMinutes}`;
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitError('');
    setSubmitSuccess(false);
    
    try {
      console.log('Updating report with values:', values);
      
      // Validate project selection
      if (!values.project_id) {
        throw new Error('Please select a project');
      }
      
      // Validate task details
      if (!values.task_details || values.task_details.length === 0) {
        throw new Error('Please add at least one task');
      }
      
      // Check for empty task descriptions or hours
      const invalidTasks = values.task_details.filter(
        task => !task.description || !task.hours || isNaN(parseFloat(task.hours))
      );
      
      if (invalidTasks.length > 0) {
        throw new Error('Please fill in all task descriptions and hours');
      }
      
      // Calculate total hours from task details
      const totalHours = calculateTotalHours(values.task_details);
      console.log('Calculated total hours:', totalHours);
      
      if (totalHours <= 0) {
        throw new Error('Total hours must be greater than zero');
      }
      
      // Add hours_worked to the values
      const reportData = {
        ...values,
        hours_worked: totalHours
      };
      
      // Ensure project_id is a number
      if (reportData.project_id) {
        reportData.project_id = Number(reportData.project_id);
      }
      
      console.log('Sending updated report data:', reportData);
      
      // Call the update API
      const response = await reportService.updateReport(id, reportData);
      console.log('Report update successful:', response);
      
      setSubmitSuccess(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/reports/${id}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating report:', error);
      
      // Handle specific error messages
      if (error.response) {
        console.error('Error response:', error.response.data);
        // Handle specific backend errors
        if (error.response.data.msg === 'Reports can only be edited on the same day they were created') {
          setSubmitError(error.response.data.msg);
          setTimeout(() => {
            navigate('/reports');
          }, 3000);
        } else if (error.response.data.msg === 'The selected project does not exist') {
          setSubmitError('The selected project is no longer available. Please choose another project.');
        } else if (error.response.status === 401) {
          setSubmitError('Your session has expired. Please log in again.');
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          setSubmitError(error.response.data.msg || 'Failed to update report');
        }
      } else if (error.request) {
        console.error('No response received from server');
        setSubmitError('No response received from server. Please check your connection and try again.');
      } else {
        console.error('Error message:', error.message);
        setSubmitError(error.message || 'An error occurred while updating your report. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loadingReport) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading report data...</p>
      </Container>
    );
  }
  
  if (!report) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          Report not found or you don't have permission to edit it.
        </Alert>
        <Button variant="primary" onClick={() => navigate('/reports')}>
          Back to Reports
        </Button>
      </Container>
    );
  }
  
  // Prepare initial values from the report data
  const initialValues = {
    date: report.created_at.split('T')[0],
    project_id: report.project_id ? Number(report.project_id) : (projects.length > 0 ? Number(projects[0].id) : ''),
    tasks: report.tasks || '',
    blockers: report.blockers || '',
    tomorrow_plan: report.tomorrow_plan || '',
    task_details: report.task_details && report.task_details.length > 0 
      ? report.task_details.map(task => ({
          description: task.task_description,
          hours: task.hours,
          project_id: task.project_id ? Number(task.project_id) : (report.project_id ? Number(report.project_id) : (projects.length > 0 ? Number(projects[0].id) : ''))
        }))
      : [{ description: '', hours: '', project_id: report.project_id ? Number(report.project_id) : (projects.length > 0 ? Number(projects[0].id) : '') }]
  };
  

  return (
    <Container>
      <Row className="justify-content-center">
        <Col md={8}>
          <Card>
            <Card.Header as="h4" className="bg-warning text-white">
              Edit Daily Report
            </Card.Header>
            <Card.Body>
              {submitError && (
                <Alert variant="danger">{submitError}</Alert>
              )}
              
              {submitSuccess && (
                <Alert variant="success">
                  Report updated successfully! Redirecting...
                </Alert>
              )}
              
              <Formik

                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                
                enableReinitialize
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting
                }) => {
                  console.log("errors",errors);
                 return (
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="date"
                          value={values.date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.date && errors.date}
                          disabled
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.date}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="hidden"
                          name="project_id"
                          value={values.project_id}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Summary of Tasks</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="tasks"
                          value={values.tasks}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.tasks && errors.tasks}
                          placeholder="Provide a brief summary of your work today"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.tasks}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className="mb-0">Task Details</Form.Label>
                          <div>
                            <span className="me-2">
                              Total Hours: <strong>{formatHoursToHoursMinutes(calculateTotalHours(values.task_details))}</strong>
                              <div className="text-muted small">({formatDecimalHours(calculateTotalHours(values.task_details))} hours)</div>
                            </span>
                          </div>
                        </div>
                        
                        <FieldArray name="task_details">
                          {({ push, remove }) => (
                            <div>
                              {values.task_details.map((task, index) => (
                                <div key={index} className="mb-3">
                                  <Row>
                                    <Col xs={12} md={4}>
                                      <Form.Group className="mb-2">
                                        <Form.Label className="small">Project</Form.Label>
                                        <Form.Select
                                          name={`task_details.${index}.project_id`}
                                          value={task.project_id}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          isInvalid={
                                            touched.task_details && 
                                            touched.task_details[index] && 
                                            touched.task_details[index].project_id && 
                                            errors.task_details && 
                                            errors.task_details[index] && 
                                            errors.task_details[index].project_id
                                          }
                                          disabled={isLoading}
                                        >
                                          <option value="">Select project</option>
                                          {projects.map(project => (
                                            <option key={project.id} value={project.id}>
                                              {project.name}
                                            </option>
                                          ))}
                                        </Form.Select>
                                        {touched.task_details && 
                                         touched.task_details[index] && 
                                         touched.task_details[index].project_id && 
                                         errors.task_details && 
                                         errors.task_details[index] && 
                                         errors.task_details[index].project_id && (
                                          <Form.Control.Feedback type="invalid">
                                            {errors.task_details[index].project_id}
                                          </Form.Control.Feedback>
                                        )}
                                      </Form.Group>
                                    </Col>
                                    <Col xs={12} md={5}>
                                      <Form.Group className="mb-2">
                                        <Form.Label className="small">Task Description</Form.Label>
                                        <Form.Control
                                          name={`task_details.${index}.description`}
                                          value={task.description}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          isInvalid={
                                            touched.task_details && 
                                            touched.task_details[index] && 
                                            touched.task_details[index].description && 
                                            errors.task_details && 
                                            errors.task_details[index] && 
                                            errors.task_details[index].description
                                          }
                                          placeholder="Task description"
                                        />
                                        {touched.task_details && 
                                         touched.task_details[index] && 
                                         touched.task_details[index].description && 
                                         errors.task_details && 
                                         errors.task_details[index] && 
                                         errors.task_details[index].description && (
                                          <Form.Control.Feedback type="invalid">
                                            {errors.task_details[index].description}
                                          </Form.Control.Feedback>
                                        )}
                                      </Form.Group>
                                    </Col>
                                    <Col xs={12} md={2}>
                                      <Form.Group className="mb-2">
                                        <Form.Label className="small">Hours</Form.Label>
                                        <InputGroup>
                                          <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            name={`task_details.${index}.hours`}
                                            value={task.hours}
                                            onChange={(e) => {
                                              // Get the value entered
                                              let rawVal = e.target.value;
                                              
                                              // Don't allow negative values
                                              if (rawVal < 0) rawVal = 0;
                                              
                                              // Process the raw value first
                                              handleChange(e);
                                            }}
                                            onBlur={(e) => {
                                              // On blur, normalize the hours format
                                              const normalizedVal = normalizeHoursInput(e.target.value);
                                              
                                              // Set the normalized value
                                              const fakeEvent = {
                                                target: {
                                                  name: e.target.name,
                                                  value: normalizedVal
                                                }
                                              };
                                              
                                              // Update the form with the normalized value
                                              handleChange(fakeEvent);
                                              handleBlur(e);
                                            }}
                                            isInvalid={
                                              touched.task_details && 
                                              touched.task_details[index] && 
                                              touched.task_details[index].hours && 
                                              errors.task_details && 
                                              errors.task_details[index] && 
                                              errors.task_details[index].hours
                                            }
                                            placeholder="Hours"
                                          />
                                          <InputGroup.Text>hrs</InputGroup.Text>
                                          {touched.task_details && 
                                           touched.task_details[index] && 
                                           touched.task_details[index].hours && 
                                           errors.task_details && 
                                           errors.task_details[index] && 
                                           errors.task_details[index].hours && (
                                            <Form.Control.Feedback type="invalid">
                                              {errors.task_details[index].hours}
                                            </Form.Control.Feedback>
                                          )}
                                        </InputGroup>
                                      </Form.Group>
                                    </Col>
                                    <Col xs={1} className="d-flex align-items-center">
                                      {values.task_details.length > 1 && (
                                        <Button 
                                          variant="outline-danger" 
                                          size="sm"
                                          onClick={() => remove(index)}
                                        >
                                          <i className="bi bi-trash"></i>
                                        </Button>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              ))}
                              
                              <Button
                                type="button"
                                variant="outline-primary"
                                onClick={() => push({ description: '', hours: '', project_id: '' })}
                                className="mt-2"
                              >
                                <i className="bi bi-plus"></i> Add Task
                              </Button>
                            </div>
                          )}
                        </FieldArray>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Blockers (Optional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="blockers"
                          value={values.blockers}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Describe any blockers or challenges you faced"
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Tomorrow's Plan (Optional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="tomorrow_plan"
                          value={values.tomorrow_plan}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Describe what you plan to work on tomorrow"
                        />
                      </Form.Group>
                      
                      <div className="d-flex justify-content-between">
                        <Button 
                          variant="secondary" 
                          onClick={() => navigate(`/reports/${id}`)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          variant="warning" 
                          type="submit" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Updating...
                            </>
                          ) : (
                            'Update Report'
                          )}
                        </Button>
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EditReport;
